import React, { useState } from "react";
import { api, errMsg, toPaise, fmtDate, fmtDateTime } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAsyncData, useStoreDayLock } from "@/hooks/useAsyncData";
import { Money, MoneyInput, StoreDatePicker, EmptyState, DayLockBanner, LoadErrorState, LoadingState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Wallet, ArrowRight, AlertTriangle, SendHorizontal } from "lucide-react";
import { toast } from "sonner";

export default function AllocationPage() {
  const { user, today, storeId, date } = useApp();
  const effStore = user.role === "cashier" ? user.store_id : storeId;
  const effDate = user.role === "cashier" ? today : date;
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjAmount, setAdjAmount] = useState("");
  const [adjReason, setAdjReason] = useState("");

  const { storeDay, refresh: refreshLock } = useStoreDayLock(effStore, effDate);

  const q = useAsyncData(
    () => {
      if (!effStore || !effDate) return Promise.resolve(null);
      return api.get("/opening-cash", { params: { store_id: effStore, business_date: effDate } }).then((r) => r.data);
    },
    [effStore, effDate]
  );
  const data = q.data;

  const header = (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div>
        <h1 className="font-display text-xl font-semibold arch-underline">Opening cash</h1>
        <p className="text-sm text-muted-foreground">One shared drawer per store — previous day's counted cash, less whatever went to HQ from that close</p>
      </div>
      <StoreDatePicker />
    </div>
  );

  if (q.error) return <div className="space-y-5">{header}<LoadErrorState error={q.error} onRetry={q.reload} title="Could not load opening cash" /></div>;
  if (q.loading) return <div className="space-y-5">{header}<LoadingState /></div>;
  if (!data) return <div className="space-y-5">{header}<EmptyState icon={Wallet} title="Select a store and date" /></div>;

  const dayFinalized = data.store_day.status === "finalized";
  const adjPaise = toPaise(adjAmount);
  const previewOpening = (data.opening_paise ?? 0) + adjPaise;
  const adj = data.opening_adjustment;
  const sends = data.hq_sends || [];

  const saveAdj = async () => {
    if (!adjReason.trim()) { toast.error("Reason is compulsory"); return; }
    if (!adjPaise) { toast.error("Enter a non-zero amount (negative allowed)"); return; }
    try {
      await api.post("/opening-cash/adjustment", {
        store_id: effStore, business_date: effDate, amount_paise: adjPaise, reason: adjReason,
      });
      toast.success("Opening adjustment approved");
      setAdjOpen(false); setAdjAmount(""); setAdjReason("");
      q.refresh(); refreshLock();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5">
      {header}

      <DayLockBanner storeDay={storeDay || data.store_day} />

      {data.carry_blocked && (
        <div className="rounded-lg border border-[hsl(var(--danger))]/45 bg-[hsl(var(--danger))]/8 px-4 py-3 space-y-1 text-sm"
          data-testid="carry-blocked-banner" role="alert">
          <p className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-[hsl(var(--danger))] mt-0.5 shrink-0" />
            <span>
              <b className="text-[hsl(var(--danger))]">Opening not carried forward.</b>{" "}
              {(data.missing_count_dates || []).map((d) => fmtDate(d)).join(", ")} traded in cash but has no
              closing cash count. Cash is never carried silently across an uncounted trading day — record that
              day's count, then this opening will appear on its own.
            </span>
          </p>
          {(data.missing_counts || []).map((g) => (
            <p key={g.business_date} className="text-[11px] text-muted-foreground pl-6" data-testid={`missing-count-${g.business_date}`}>
              {fmtDate(g.business_date)} — {g.bill_count} bill(s), net cash movement <Money paise={g.net_cash_paise} className="text-foreground" />
            </p>
          ))}
        </div>
      )}

      {data.needs_revalidation && (
        <div className="rounded-lg border border-[hsl(var(--danger))]/45 bg-[hsl(var(--danger))]/8 px-4 py-3 flex items-start gap-2 text-sm"
          data-testid="carry-revalidation-banner" role="alert">
          <AlertTriangle className="h-4 w-4 text-[hsl(var(--danger))] mt-0.5 shrink-0" />
          <p>
            This finalized day's recorded opening no longer matches the recomputed carry
            (difference <Money paise={data.carry_mismatch_paise} signed colored />). Nothing has been rewritten —
            an admin must reopen and re-finalize this day to correct it.
          </p>
        </div>
      )}

      {data.opening_provisional && !dayFinalized && (
        <div className="rounded-lg border border-[hsl(var(--warning))]/45 bg-[hsl(var(--warning))]/10 px-4 py-2.5 flex items-start gap-2 text-sm"
          data-testid="provisional-opening-banner" role="status">
          <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))] mt-0.5 shrink-0" />
          <p>
            <b>Provisional opening</b> — taken from the counted close of {fmtDate(data.opening_source_date)}, which is not finalized yet.
            Entries can continue as usual; the figure is re-confirmed the moment that day is finalized.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 min-[480px]:grid-cols-3 gap-2.5">
        <Card className="rounded-lg"><CardContent className="p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Carried opening</p>
          <p className="text-lg font-semibold" data-testid="carried-opening"><Money paise={data.opening_paise} /></p>
          <p className="text-[11px] text-muted-foreground">
            {data.opening_source === "carry"
              ? <>counted close of {fmtDate(data.opening_source_date)} − HQ sends</>
              : "first day for this store"}
          </p>
        </CardContent></Card>
        <Card className="rounded-lg"><CardContent className="p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Admin adjustment</p>
          <p className="text-lg" data-testid="opening-adjustment-amount-display"><Money paise={adj?.amount_paise ?? 0} signed colored={!!adj} /></p>
          {adj && <p className="text-[11px] text-muted-foreground truncate" title={adj.reason}>{adj.reason} — {adj.approved_by_name}</p>}
        </CardContent></Card>
        <Card className="rounded-lg border-[hsl(var(--brass))]/50"><CardContent className="p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Effective opening</p>
          <p className="text-lg font-semibold glow-sapphire" data-testid="effective-opening"><Money paise={data.effective_opening_paise} /></p>
          <p className="text-[11px] text-muted-foreground">starting point of today's expected cash</p>
        </CardContent></Card>
      </div>

      {data.opening_source === "carry" && (
        <Card className="rounded-lg"><CardContent className="p-4 space-y-1.5" data-testid="carry-breakdown">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">How this opening was worked out</p>
          <div className="flex justify-between text-sm">
            <span>Counted cash at the close of {fmtDate(data.opening_source_date)}</span>
            <Money paise={data.prev_close_counted_paise} />
          </div>
          {sends.map((t) => (
            <div key={t.id} className="flex justify-between text-sm text-muted-foreground" data-testid={`carry-hq-send-${t.id.slice(0, 6)}`}>
              <span className="flex items-center gap-1.5 min-w-0">
                <SendHorizontal className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--danger))]" />
                <span className="truncate">
                  Sent to HQ {t.by ? `by ${t.by}` : ""} — entered {fmtDateTime(t.entered_at)}
                  {t.note ? ` · ${t.note}` : ""}
                </span>
              </span>
              <Money paise={-t.amount_paise} className="text-[hsl(var(--danger))]" />
            </div>
          ))}
          {sends.length === 0 && <p className="text-xs text-muted-foreground">No cash was sent to HQ from that close.</p>}
          <div className="flex justify-between text-sm font-semibold border-t pt-1.5">
            <span>Carried into {fmtDate(effDate)}</span>
            <Money paise={data.opening_paise} />
          </div>
        </CardContent></Card>
      )}

      <Card className="rounded-lg"><CardContent className="p-4 text-sm text-muted-foreground flex items-start gap-2">
        <ArrowRight className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--brass))]" />
        <p>
          Opening cash for <span className="font-mono-num text-foreground">{fmtDate(effDate)}</span> is not split between cashiers.
          Every cashier's bills, receipts and expenses flow into the same store drawer, and one closing count is submitted for the store.
        </p>
      </CardContent></Card>

      {user.role === "admin" && !dayFinalized && (
        <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" data-testid="opening-adjustment-button">Admin: opening adjustment</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Approved opening adjustment</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">
              Enter a positive amount to add to the carried opening, or a negative amount (e.g. <span className="font-mono-num">-500</span>) to reduce it.
              It is kept separate from the carry, a reason is compulsory and the change is audited.
            </p>
            <MoneyInput value={adjAmount} onChange={setAdjAmount} allowNegative testId="opening-adjustment-amount" placeholder="e.g. 500 or -500" />
            {adjAmount !== "" && adjAmount !== "-" && (
              <p className="text-xs" data-testid="opening-adjustment-preview">
                Carried opening <Money paise={data.opening_paise} /> {adjPaise >= 0 ? "+" : "−"} <Money paise={Math.abs(adjPaise)} />
                {" "}→ effective opening{" "}
                <Money paise={previewOpening} className={previewOpening < 0 ? "money-neg" : "font-semibold"} />
                {previewOpening < 0 && <span className="text-[hsl(var(--danger))] ml-1">cannot go below zero</span>}
              </p>
            )}
            <Textarea value={adjReason} onChange={(e) => setAdjReason(e.target.value)} placeholder="Reason (compulsory)" data-testid="opening-adjustment-reason" />
            <DialogFooter>
              <Button onClick={saveAdj} disabled={!adjReason.trim() || !adjPaise || previewOpening < 0} data-testid="opening-adjustment-save">Approve</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
